import { createSlice } from '@reduxjs/toolkit';

import { StatusTransport } from '@pages/hubs/Garage/enum/statusTransport.enums';
import { createFormStepper, formStepper } from '@pages/hubs/Work/const/create-form-stepper';
import { isListOver } from '@shared/functions/isListOver';
import { changeCoverPhoto } from '@shared/utils/hubs/changeCoverPhoto';

import { CreateWorkStepsEnum } from '../enum/create-work-steps-enum';
import { WorkTypeEnum } from '../enum/work-type-enum';

import { acceptedWorkWS, addWorkWS, getWorks, updateWorkWS } from './work-thunk';

import type { RootWorkData, RootWorkItemsDataStep } from './work-types';
import type { RootMeta } from '@pages/hubs/Garage/store/types';
import type { GetHubsParams } from '@pages/hubs/Pets/store/pets-types';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AssignPeoplePermissionsEnum } from '@shared/enums/assignPeoplePermissions.enum';
import type { AssignPeopleSelectValueModel } from '@shared/models/assignPeopleSelectValue.model';
import type { FilterParams } from '@shared/models/hubs/hub-filters.model';
import type { FormStepper } from '@shared/models/hubs/hub-form-stepper.model';
import type { MediaType } from '@shared/models/media.model';

const initialState = {
  isLoadingWork: false,
  works: {
    data: [] as RootWorkData[],
    meta: {} as RootMeta,
  },
  currentWork: {} as RootWorkData,
  isListOver: false as boolean | number,
  isEmptyWorkWithFilter: false,
  filterParams: {
    query: '',
    shared_filter: [],
  } as FilterParams,
  editMode: false,

  share: {
    sharePeopleList: [] as AssignPeopleSelectValueModel[],
    selectUser: null as AssignPeopleSelectValueModel | null,
  },

  createWork: {
    formStepper: formStepper as FormStepper<CreateWorkStepsEnum>,
    currentStep: CreateWorkStepsEnum.preStepWorkType,
    workType: WorkTypeEnum.Custom,
    draftItem: null as RootWorkData | null,
    isCanChangeCurrentStep: true,
  },
};

type InitialStateType = typeof initialState;
export const workSlice = createSlice({
  name: 'workSlice',
  initialState: initialState as InitialStateType,
  reducers: {
    // ------------------ common ------------------
    setLoadingWork(state, action: PayloadAction<{ isLoading: boolean }>) {
      state.isLoadingWork = action.payload.isLoading;
    },
    clearWorkFilters(state) {
      state.filterParams = { ...state.filterParams, query: '' };
    },
    // ------------------ main page ------------------
    setWorkFilterParams(state, action: PayloadAction<{ params: FilterParams }>) {
      state.filterParams = action.payload.params;
    },
    removeCurrentWork(state, action: PayloadAction<{ workID: number }>) {
      const index = state.works.data.findIndex((work) => work.id === action.payload.workID);
      if (index !== -1) state.works.data.splice(index, 1);

      if (
        !state.works.data.length &&
        (!!state.filterParams.shared_filter.length || !!state.filterParams.query)
      ) {
        state.isEmptyWorkWithFilter = true;
      }
    },
    setWorksDataWithNewPosition(state, action: PayloadAction<{ newPositionData: RootWorkData[] }>) {
      state.works.data = action.payload.newPositionData;
    },
    isEmptyDataWorkWithFilter(
      state,
      action: PayloadAction<{
        filters: Pick<GetHubsParams, 'shared_filter' | 'query'>;
        worksData: RootWorkData[];
      }>,
    ) {
      const { shared_filter, query } = action.payload.filters;
      state.isEmptyWorkWithFilter = (!!shared_filter?.length || !!query) && !action.payload.worksData.length;
    },
    clearWorksState(state) {
      state.works = initialState.works;
    },
    // ------------------ view work ------------------
    setCurrentWork(state, action: PayloadAction<{ work: RootWorkData }>) {
      state.currentWork = action.payload.work;
    },
    updateWorkByField<T extends keyof RootWorkData>(
      state: { currentWork: RootWorkData },
      action: PayloadAction<{ field: T; value: RootWorkData[T] }>,
    ) {
      const { field, value } = action.payload;
      state.currentWork = {
        ...state.currentWork,
        [field]: value,
      };
    },
    setEditMode(state, action: PayloadAction<{ editMode: boolean }>) {
      state.editMode = action.payload.editMode;
    },
    // ------------------ gallery work ------------------
    addGalleryPhoto(state, action: PayloadAction<{ newGalleryPhotos: MediaType[] }>) {
      state.currentWork.photos = action.payload.newGalleryPhotos;
    },
    updateGalleryCoverPhoto(state, action: PayloadAction<{ coverPhoto: MediaType }>) {
      state.currentWork.cover_photo = action.payload.coverPhoto;
    },
    removeGalleryCoverPhoto(
      state,
      action: PayloadAction<{ deleteCoverID: number; isCover: boolean | undefined }>,
    ) {
      const deleteCoverIndex = state.currentWork.photos.findIndex(
        (photo) => photo.id === action.payload.deleteCoverID,
      );
      if (deleteCoverIndex !== -1) state.currentWork.photos.splice(deleteCoverIndex, 1);
      if (!!state.currentWork.photos.length) {
        const { photos } = changeCoverPhoto(state.currentWork.photos);

        const hasCoverPhoto = state.currentWork.photos.filter((photo) => photo.is_cover);

        state.currentWork.photos = [...photos];

        if (!hasCoverPhoto.length) {
          state.currentWork.cover_photo = photos.length ? photos[photos.length - 1] : null;
        }
      } else state.currentWork.cover_photo = null;
    },
    // ------------------ files work ------------------
    addWorkDocuments(state, action: PayloadAction<{ newDocuments: MediaType[] }>) {
      state.currentWork.documents = action.payload.newDocuments;
    },
    // ------------------ share work ------------------
    setAssignPeopleLists(state, action: PayloadAction<{ peopleLists: AssignPeopleSelectValueModel[] }>) {
      state.share.sharePeopleList = action.payload.peopleLists;
    },
    setSelectedUser(state, action: PayloadAction<{ selectUser: AssignPeopleSelectValueModel | null }>) {
      state.share.selectUser = action.payload.selectUser;
    },
    addShareUser(state, action: PayloadAction<{ shareUser: AssignPeopleSelectValueModel }>) {
      state.currentWork.users.push(action.payload.shareUser);
    },
    deleteWorkShareUser(state, action: PayloadAction<{ shareUserID: number }>) {
      const index = state.currentWork.users.findIndex((user: any) => user.id === action.payload.shareUserID);
      if (index !== -1) state.currentWork.users.splice(index, 1);
    },
    updateShareUser(
      state,
      action: PayloadAction<{ userRole: AssignPeoplePermissionsEnum; shareUserID: number }>,
    ) {
      const index = state.currentWork.users.findIndex((user: any) => user.id === action.payload.shareUserID);
      if (index !== -1) state.currentWork.users[index].role = action.payload.userRole;
    },
    // ------------------ create work ------------------
    setWorkCreateType: (state, action: PayloadAction<WorkTypeEnum>) => {
      state.createWork.workType = action.payload;
    },
    setWorkCoverPhoto: (state, action: PayloadAction<{ cover_photo: MediaType | null }>) => {
      if (state.createWork.draftItem) {
        state.createWork.draftItem.cover_photo = action.payload.cover_photo;
      }
    },
    setWorkCreateDraftItem: (state, action: PayloadAction<RootWorkData | null>) => {
      state.createWork.draftItem = action.payload;
      if (action.payload) {
        state.createWork.workType = action.payload.employment_type;
      }
    },
    updateWorkDraftItem: (state, action: PayloadAction<any | null>) => {
      state.createWork.draftItem = { ...state.createWork.draftItem, ...action.payload };
    },
    setWorkCreateCurrentStep: (state, action: PayloadAction<CreateWorkStepsEnum>) => {
      state.createWork.currentStep = action.payload;
      state.createWork.isCanChangeCurrentStep = true;
    },
    changeWorkCreateFormStepperFromDraftItem: (state, action: PayloadAction<RootWorkItemsDataStep[]>) => {
      const enabledSteps = new Set([CreateWorkStepsEnum.preStepWorkType, CreateWorkStepsEnum.generalBase]);

      action.payload.forEach(({ step }) => {
        const currentStep = state.createWork.formStepper[step];
        if (currentStep) {
          currentStep.isDisabled = enabledSteps.has(step);
          currentStep.isPassed = true;
        }
      });

      const lastStepInPayload = action.payload[action.payload.length - 1];
      if (lastStepInPayload) {
        state.createWork.currentStep =
          createFormStepper[createFormStepper.findIndex((item) => item === lastStepInPayload.step) + 1];
      }
    },
    setWorkCreateFormStepPassed: (
      state,
      action: PayloadAction<{ step: CreateWorkStepsEnum; isPassed?: boolean }>,
    ) => {
      const { step, isPassed = true } = action.payload;
      if (state.createWork.formStepper[step]) {
        state.createWork.formStepper[step].isPassed = isPassed;
      }
    },
    setWorkCreateFormStepDisabled: (
      state,
      action: PayloadAction<{ step: CreateWorkStepsEnum; isDisabled?: boolean }>,
    ) => {
      const { step, isDisabled = true } = action.payload;
      if (state.createWork.formStepper[step]) {
        state.createWork.formStepper[step].isDisabled = isDisabled;
      }
    },
    setWorkCreateFormStepSkip: (
      state,
      action: PayloadAction<{ step: CreateWorkStepsEnum; isSkip?: boolean }>,
    ) => {
      const { step, isSkip = true } = action.payload;
      if (state.createWork.formStepper[step]) {
        state.createWork.formStepper[step].isSkip = isSkip;
      }
    },
    setWorkCreateCanChangeCurrentStep: (state, action: PayloadAction<boolean>) => {
      state.createWork.isCanChangeCurrentStep = action.payload;
    },
    changeWorkCreateFormStepperPassedDisableSteps: (
      state,
      action: PayloadAction<{
        enabledSteps: CreateWorkStepsEnum[];
        passedStep: RootWorkItemsDataStep[];
      }>,
    ) => {
      const enabledSteps = new Set(action.payload.enabledSteps);

      action.payload.passedStep.forEach(({ step }) => {
        const currentStep = state.createWork.formStepper[step];
        if (currentStep) {
          currentStep.isDisabled = enabledSteps.has(step);
          currentStep.isPassed = true;
        }
      });
    },

    resetCreateStore: (state) => {
      state.createWork = {
        formStepper: formStepper,
        currentStep: CreateWorkStepsEnum.preStepWorkType,
        workType: WorkTypeEnum.Custom,
        draftItem: null,
        isCanChangeCurrentStep: true,
      };
    },

    updateWorkCoverPhotoWS(state, action: PayloadAction<{ newCoverPhoto: MediaType; workID: number }>) {
      const index = state.works.data.findIndex((work) => work.id === action.payload.workID);

      if (index !== -1) state.works.data[index].cover_photo = action.payload.newCoverPhoto;
    },
  },

  extraReducers: ({ addCase }) => {
    // ------------------ main page ------------------
    addCase(getWorks.fulfilled, (state, { payload }) => {
      if (payload.meta.current_page === 1) {
        state.works.data = payload.data;
        state.works.meta = payload.meta;
        state.isListOver = isListOver(payload);
      } else {
        state.works.data.push(...payload.data);
        state.works.meta = payload.meta;
        state.isListOver = isListOver(payload);
      }
    });
    // ------------------ web-socket ------------------
    addCase(updateWorkWS.fulfilled, (state, action: PayloadAction<{ workWS: RootWorkData }>) => {
      if (!!state.works.data.length) {
        const index = state.works.data.findIndex((work) => work.id === action.payload.workWS.id);

        if (index !== -1) state.works.data[index] = action.payload.workWS;
      }
    });
    addCase(addWorkWS.fulfilled, (state, action: PayloadAction<{ addWorkWS: RootWorkData }>) => {
      const index = state.works.data.findIndex((work) => work.id === action.payload.addWorkWS.id);

      if (index !== -1) {
        state.works.data[index] = action.payload.addWorkWS;
      } else {
        state.works.data = !!state.works.data.length
          ? [action.payload.addWorkWS, ...state.works.data]
          : [action.payload.addWorkWS];
      }
    });
    addCase(acceptedWorkWS.fulfilled, (state, action: PayloadAction<{ acceptedWorkWS: RootWorkData }>) => {
      const index = state.works.data.findIndex((work) => work.id === action.payload.acceptedWorkWS.id);
      state.works.data.splice(index, 1);

      const pending = state.works.data.filter(
        (work) => work.current.confirm_status === StatusTransport.pending,
      );
      const general = state.works.data.filter(
        (work) => work.current.confirm_status !== StatusTransport.pending,
      );

      state.works.data = [...pending, action.payload.acceptedWorkWS, ...general];
    });
  },
});

export const {
  setWorkFilterParams,
  setLoadingWork,
  isEmptyDataWorkWithFilter,
  removeCurrentWork,
  setWorksDataWithNewPosition,
  clearWorksState,
  resetCreateStore,
  setWorkCreateType,
  setWorkCreateDraftItem,
  changeWorkCreateFormStepperFromDraftItem,
  setWorkCreateCurrentStep,
  setWorkCreateFormStepPassed,
  setWorkCreateFormStepDisabled,
  setWorkCreateFormStepSkip,
  setWorkCreateCanChangeCurrentStep,
  changeWorkCreateFormStepperPassedDisableSteps,
  setWorkCoverPhoto,
  setCurrentWork,
  setEditMode,
  addGalleryPhoto,
  updateGalleryCoverPhoto,
  removeGalleryCoverPhoto,
  addWorkDocuments,
  setAssignPeopleLists,
  setSelectedUser,
  addShareUser,
  deleteWorkShareUser,
  updateShareUser,
  updateWorkCoverPhotoWS,
  clearWorkFilters,
  updateWorkDraftItem,
  updateWorkByField,
} = workSlice.actions;

export const workReducer = workSlice.reducer;
